import {
  CALCULATOR_RUNTIME_MANIFEST_URL,
  parseCalculatorRuntimeManifest,
} from "./calculator-runtime-manifest.js";

function resolveUrl(value, baseUrl) {
  try {
    return new URL(value, baseUrl).toString();
  } catch (error) {
    throw new TypeError(`invalid calculator runtime URL: ${String(value)}`, {
      cause: error,
    });
  }
}

function defaultBaseUrl() {
  return globalThis.location?.href || "https://fishystuff.fish/";
}

export async function loadCalculatorRuntimeManifest({
  expectedEngineVersion,
  manifestUrl = CALCULATOR_RUNTIME_MANIFEST_URL,
  baseUrl = defaultBaseUrl(),
  fetchImpl = globalThis.fetch?.bind(globalThis),
} = {}) {
  if (typeof expectedEngineVersion !== "string" || expectedEngineVersion.length === 0) {
    throw new TypeError("calculator runtime loading requires expectedEngineVersion");
  }
  if (typeof fetchImpl !== "function") {
    throw new TypeError("calculator runtime loading requires fetch()");
  }

  const resolvedManifestUrl = resolveUrl(manifestUrl, baseUrl);
  const response = await fetchImpl(resolvedManifestUrl, { cache: "no-store" });
  if (!response?.ok) {
    throw new Error(
      `failed to load calculator runtime manifest: ${resolvedManifestUrl} (${response?.status || "unknown"})`,
    );
  }

  let decoded;
  try {
    decoded = await response.json();
  } catch (error) {
    throw new Error(
      `failed to decode calculator runtime manifest: ${resolvedManifestUrl}`,
      { cause: error },
    );
  }
  const manifest = parseCalculatorRuntimeManifest(decoded);
  if (manifest.engineVersion !== expectedEngineVersion) {
    throw new Error(
      `calculator runtime engineVersion mismatch: expected ${expectedEngineVersion}, got ${manifest.engineVersion}`,
    );
  }

  return Object.freeze({
    ...manifest,
    manifestUrl: resolvedManifestUrl,
    moduleUrl: resolveUrl(manifest.module, resolvedManifestUrl),
    wasmUrl: resolveUrl(manifest.wasm, resolvedManifestUrl),
  });
}

export async function loadCalculatorRuntimeModule({
  importModuleImpl = (url) => import(url),
  ...options
} = {}) {
  if (typeof importModuleImpl !== "function") {
    throw new TypeError("calculator runtime loading requires a module importer");
  }
  const runtime = await loadCalculatorRuntimeManifest(options);
  const module = await importModuleImpl(runtime.moduleUrl);
  if (typeof module?.default !== "function") {
    throw new TypeError("calculator runtime module is missing its Wasm initializer");
  }
  await module.default({ module_or_path: runtime.wasmUrl });
  return Object.freeze({ ...runtime, module });
}
